import { useEffect, useRef } from 'react';
import { QuilttProvider } from '@quiltt/react';
import { useSnackbar } from 'notistack';

import { TaskStatus } from 'modules/common/components/MemberOperator/MemberOperator.interfaces';
import useQuilttConnect from 'modules/common/components/StatementUpload/hooks/useQuilttConnect';
import { BaseTaskContext, SubmitActionTask } from 'modules/Tasks/types';
import useInvalidateMemberTaskQueries from 'services/memberTasks/useInvalidateMemberTaskQueries';
import useUpdateOperatorTaskStatus from 'services/operatorTasks/mutations/useUpdateOperatorTaskStatus';
import useQuilttSessionToken from 'services/quiltt/queries/useQuilttSessionToken';

interface ReconnectQuilttContext extends BaseTaskContext {
    institution_id: string;
    institution_name?: string;
    connection_id: string;
    connector_id: string;
    error_repairable_at?: string | null;
    webhook_received_at?: string | null;
    webhook_connection_id?: string | null;
}

interface ReconnectQuilttModalProps {
    open: boolean;
    onClose: () => void;
    task: SubmitActionTask;
}

function isReconnectQuilttContext(ctx: unknown): ctx is ReconnectQuilttContext {
    if (typeof ctx !== 'object' || ctx === null) {
        return false;
    }
    const candidate = ctx as Record<string, unknown>;
    return typeof candidate.connection_id === 'string' && typeof candidate.connector_id === 'string';
}

interface ReconnectQuilttModalContentProps {
    open: boolean;
    onClose: () => void;
    context: ReconnectQuilttContext;
    taskId: string;
}

function ReconnectQuilttModalContent({ open, onClose, context, taskId }: Readonly<ReconnectQuilttModalContentProps>) {
    const invalidateMemberTaskQueries = useInvalidateMemberTaskQueries();
    const { mutate: updateTaskStatus } = useUpdateOperatorTaskStatus();
    const { enqueueSnackbar } = useSnackbar();
    const launchedRef = useRef(false);

    const { open: openQuilttConnector } = useQuilttConnect({
        connectorId: context.connector_id,
        connectionId: context.connection_id,
        institutionName: context.institution_name ?? '',
        sourceSurface: 'member_task_reconnect',
        onSuccess: (metadata) => {
            // useQuilttConnect only invokes onSuccess once the connector returns a connectionId, so it
            // is always present here. Optimistically complete the task now; the reconnect webhook still
            // confirms server-side (double validation) and remains the backstop if this call fails.
            updateTaskStatus(
                {
                    id: taskId,
                    status: TaskStatus.COMPLETED,
                    completion_payload: { connection_id: metadata.connectionId },
                },
                {
                    // Same handling on success or failure: refetch + close. If the optimistic
                    // completion fails, the webhook stays the source of truth and completes the task.
                    onSettled: () => {
                        invalidateMemberTaskQueries();
                        onClose();
                    },
                }
            );
        },
        onAbort: () => onClose(),
        onError: () => {
            enqueueSnackbar('Could not open the reconnection flow. Please try again.', { variant: 'error' });
            onClose();
        },
    });

    // Opening the modal is the member's "Re-enable" action; launch the connector once per open.
    useEffect(() => {
        if (open && !launchedRef.current) {
            launchedRef.current = true;
            openQuilttConnector();
        }
        if (!open) {
            launchedRef.current = false;
        }
    }, [open, openQuilttConnector]);

    return null;
}

export default function ReconnectQuilttModal({ open, onClose, task }: Readonly<ReconnectQuilttModalProps>) {
    const { data: quilttSessionToken } = useQuilttSessionToken();
    const token = quilttSessionToken?.token;
    const context = isReconnectQuilttContext(task.context) ? task.context : null;

    if (!context || !token) {
        return null;
    }

    return (
        <QuilttProvider token={token}>
            <ReconnectQuilttModalContent open={open} onClose={onClose} context={context} taskId={task.id} />
        </QuilttProvider>
    );
}
